import React, { useEffect, useRef, useState } from "react";
import { http, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { applyCandidateFilters } from "../lib/filters";
import { MagnifyingGlass, Plus, X, CloudArrowUp, CheckCircle, WarningCircle, ListMagnifyingGlass, Users, ChartBar, MapPin, Trash, ArrowCounterClockwise, UserMinus, LinkedinLogo, PencilSimple, FilePdf } from "@phosphor-icons/react";

const STATUS_PILL = {
  New: "pill-new",
  Screening: "pill-screening",
  Interview: "pill-interview",
  Offer: "pill-offer",
  Hired: "pill-hired",
  Rejected: "pill-rejected",
};

export default function CandidateList({ onSelectCandidate, statusFilter = "All", locationFilter = "All", requisitionFilter = "All" }) {
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showSyncLog] = useState(false);
  const [stats, setStats] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("Send to Interview");
  const [processing, setProcessing] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (includeInactive) params.includeInactive = true;
    const [list, st] = await Promise.all([
      http.get("/candidates", { params }),
      http.get("/candidate-stats"),
    ]);
    setCandidates(list.data);
    setStats(st.data);
  };
  useEffect(() => { load(); }, [q, includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side filter by sidebar's Status + Location + Requisition filters.
  const filteredCandidates = applyCandidateFilters(candidates, statusFilter, locationFilter, requisitionFilter);

  const pickedList = candidates.filter((c) => picked.has(c.id));
  const avgPicked = pickedList.length
    ? Math.round((pickedList.reduce((s, c) => s + Number(c.matchScore || 0), 0) / pickedList.length) * 10) / 10
    : 0;
  const positionCounts = pickedList.reduce((acc, c) => {
    acc[c.position] = (acc[c.position] || 0) + 1;
    return acc;
  }, {});

  const runBulk = async () => {
    if (pickedList.length === 0) return;
    setProcessing(true);
    try {
      await http.post("/communications/bulk", {
        candidateIds: pickedList.map((c) => c.id),
        action: bulkAction,
      });
      setPicked(new Set());
      await load();
    } finally { setProcessing(false); }
  };

  return (
    <div className="space-y-5" data-testid="candidate-list-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Talent Pipeline</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Candidate List</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {filteredCandidates.length} of {candidates.length} candidates.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="add-candidate-btn"
            className="btn btn-primary"
            onClick={() => setOpenAdd(true)}
          >
            <Plus size={14} weight="bold" /> Add New Candidate
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card p-3 flex items-center gap-2">
        <MagnifyingGlass size={16} className="text-slate-400 ml-2" />
        <input
          data-testid="candidate-search"
          className="input !border-0 focus:!shadow-none"
          placeholder="Search candidates by name, email, phone, position, or skills"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer whitespace-nowrap mr-2" data-testid="include-inactive-toggle">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Show inactive
        </label>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="data-table" data-testid="candidates-table">
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>ID</th>
              <th>Name</th>
              <th>Position</th>
              <th>Location</th>
              <th>Application Date</th>
              <th>Status</th>
              <th style={{ width: 200 }}>Match Score</th>
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.map((c) => (
              <tr
                key={c.id}
                className={`cursor-pointer ${c.active === false ? "opacity-60 bg-slate-50" : ""}`}
                data-testid={`candidate-row-${c.id}`}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    data-testid={`pick-${c.id}`}
                    checked={picked.has(c.id)}
                    onChange={() => toggle(c.id)}
                    disabled={c.active === false}
                  />
                </td>
                <td onClick={() => setSelectedDetail(c)} className="font-mono text-slate-400 text-xs">{String(c.id).padStart(3, "0")}</td>
                <td onClick={() => setSelectedDetail(c)} className="font-medium">
                  {c.name}
                  {c.linkedinUrl && (
                    <a
                      href={c.linkedinUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="ml-1.5 inline-block text-[#0A66C2] hover:text-[#004182] align-middle"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`linkedin-icon-${c.id}`}
                      title={c.linkedinUrl}
                    >
                      <LinkedinLogo size={14} weight="fill" />
                    </a>
                  )}
                  {c.active === false && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-slate-200 text-slate-600 rounded-sm" data-testid={`inactive-badge-${c.id}`}>
                      Inactive
                    </span>
                  )}
                </td>
                <td onClick={() => setSelectedDetail(c)}>{c.position}</td>
                <td onClick={() => setSelectedDetail(c)}>{c.location}</td>
                <td onClick={() => setSelectedDetail(c)} className="font-mono text-xs">
                  {c.active === false && c.endDate ? `Ended ${c.endDate}` : c.applicationDate}
                </td>
                <td onClick={() => setSelectedDetail(c)}><span className={`pill ${STATUS_PILL[c.status] || "pill-new"}`}>{c.status}</span></td>
                <td>
                  <div className="flex items-center gap-3">
                    <div className="progress-track flex-1">
                      <div
                        className={`progress-fill ${
                          c.matchScore >= 80 ? "success" : c.matchScore >= 60 ? "" : "warning"
                        }`}
                        style={{ width: `${c.matchScore}%` }}
                      />
                    </div>
                    <span className="font-mono text-xs w-8 text-right">{c.matchScore}</span>
                  </div>
                </td>
              </tr>
            ))}
            {filteredCandidates.length === 0 && (
              <tr><td colSpan={8} className="text-center text-slate-400 py-10">
                {candidates.length === 0 ? "No candidates match." : "No candidates match the current filters. Change Status/Location in the left sidebar to see more."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedDetail && (
        <DetailDrawer
          candidate={selectedDetail}
          onClose={() => setSelectedDetail(null)}
          onScreen={() => { onSelectCandidate && onSelectCandidate(selectedDetail); setSelectedDetail(null); }}
          onSynced={async () => { await load(); }}
          refreshSelected={async () => {
            const r = await http.get(`/candidates/${selectedDetail.id}`);
            setSelectedDetail(r.data);
            setCandidates((prev) => prev.map((x) => (x.id === r.data.id ? r.data : x)));
          }}
        />
      )}
      {openAdd && <AddCandidateModal onClose={() => setOpenAdd(false)} onCreated={load} />}
      {showSyncLog && null}

      {pickedList.length > 0 && (
        <div className="card p-6" data-testid="selection-summary">
          <h2 className="font-display text-xl font-bold mb-1">Selection Summary</h2>
          <p className="text-sm text-slate-500 mb-4">
            Average Match Score of Selected: <span className="font-mono font-bold text-slate-800">{avgPicked}</span>
          </p>
          <h3 className="font-display font-bold mb-2">Position Breakdown</h3>
          <table className="data-table mb-4 max-w-md">
            <thead><tr><th>Position</th><th style={{ width: 80 }}>Count</th></tr></thead>
            <tbody>
              {Object.entries(positionCounts).map(([p, n]) => (
                <tr key={p}><td>{p}</td><td className="font-mono">{n}</td></tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="label-eyebrow block mb-1">Select action</label>
              <select
                data-testid="bulk-action-select"
                className="input"
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                {["Send to Interview","Move to Screening","Send to Offer","Reject","Hire"].map((a) => (
                  <option key={a}>{a}</option>
                ))}
              </select>
            </div>
            <button data-testid="bulk-process" className="btn btn-primary" onClick={runBulk} disabled={processing}>
              {processing ? "Processing…" : `Process ${pickedList.length} Candidate${pickedList.length === 1 ? "" : "s"}`}
            </button>
            <button className="btn btn-outline" onClick={() => setPicked(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {stats && (
        <div className="card p-6" data-testid="candidate-statistics">
          <h2 className="font-display text-xl font-bold mb-4">Candidate Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatTile icon={Users} label="Total Candidates" value={stats.totalCandidates} accent="#002FA7" />
            <StatTile icon={ChartBar} label="Average Match Score" value={stats.averageMatchScore} suffix="/100" accent="#059669" />
            <StatTile icon={MapPin} label="Unique Locations" value={stats.uniqueLocations} accent="#d97706" />
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, suffix, accent }) {
  return (
    <div className="border rounded-sm p-5 relative overflow-hidden">
      <div className="absolute right-3 top-3 opacity-10" style={{ color: accent }}>
        <Icon size={72} weight="fill" />
      </div>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="font-display text-4xl font-bold" style={{ color: accent }}>
        {value}{suffix && <span className="text-slate-400 text-2xl font-normal">{suffix}</span>}
      </div>
    </div>
  );
}

function FusionBadge({ candidate }) {
  if (candidate.fusionApplicationId) {
    return (
      <span
        data-testid={`fusion-badge-${candidate.id}`}
        title={`Application ${candidate.fusionApplicationId} · Req ${candidate.fusionRequisitionNumber}`}
        className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm"
      >
        <CheckCircle size={11} weight="fill" /> APP·{candidate.fusionApplicationId}
      </span>
    );
  }
  if (candidate.fusionCandidateNumber) {
    return (
      <span
        data-testid={`fusion-badge-${candidate.id}`}
        title={`Candidate ${candidate.fusionCandidateNumber}${candidate.fusionPersonId ? " · PersonId " + candidate.fusionPersonId : ""}`}
        className="inline-flex items-center gap-1 text-[11px] font-mono text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-sm"
      >
        <CheckCircle size={11} weight="fill" /> C·{candidate.fusionCandidateNumber}
      </span>
    );
  }
  if (candidate.fusionLastSyncAt && candidate.fusionLastSyncOk === false) {
    return (
      <span
        data-testid={`fusion-badge-${candidate.id}`}
        title={candidate.fusionLastSyncError || "Sync failed"}
        className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm"
      >
        <WarningCircle size={11} weight="fill" /> Failed
      </span>
    );
  }
  return (
    <span
      data-testid={`fusion-badge-${candidate.id}`}
      className="text-[11px] text-slate-400"
    >
      —
    </span>
  );
}

function DetailDrawer({ candidate, onClose, onScreen, onSynced, refreshSelected }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [submittingApp, setSubmittingApp] = useState(false);
  const [reqNumInput, setReqNumInput] = useState(candidate.fusionRequisitionNumber || "");
  const [appError, setAppError] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [reqsSource, setReqsSource] = useState(null);
  const [reqsHint, setReqsHint] = useState(null);
  const [reqsLoading, setReqsLoading] = useState(true);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [lifecycleMsg, setLifecycleMsg] = useState(null);
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerCounterMode, setOfferCounterMode] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const [offerMsg, setOfferMsg] = useState(null);
  const [holdOpen, setHoldOpen] = useState(false);
  const [linkedinDraft, setLinkedinDraft] = useState(candidate.linkedinUrl || "");
  const [linkedinBusy, setLinkedinBusy] = useState(false);
  const [linkedinMsg, setLinkedinMsg] = useState(null);
  const [linkedinPdfBusy, setLinkedinPdfBusy] = useState(false);
  const linkedinPdfRef = useRef(null);
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  // On drawer open (or when the candidate id changes), silently refresh so
  // we never render stale data left over from another page's actions.
  useEffect(() => {
    if (refreshSelected) refreshSelected().catch(() => {});
  }, [candidate.id]);

  useEffect(() => { setLinkedinDraft(candidate.linkedinUrl || ""); }, [candidate.id, candidate.linkedinUrl]);

  const saveLinkedin = async () => {
    setLinkedinBusy(true); setLinkedinMsg(null);
    try {
      await http.patch(`/candidates/${candidate.id}`, { linkedinUrl: linkedinDraft.trim() });
      await refreshSelected();
      await onSynced();
      setLinkedinMsg("Saved.");
    } catch (e) {
      setLinkedinMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLinkedinBusy(false); }
  };

  const refreshLinkedin = async () => {
    setLinkedinBusy(true); setLinkedinMsg(null);
    try {
      const r = await http.post(`/candidates/${candidate.id}/refresh-profile`, {
        url: linkedinDraft.trim() || undefined,
      });
      await refreshSelected();
      await onSynced();
      const s = r.data?.scrape || {};
      setLinkedinMsg(
        s.name || s.title || s.description
          ? `Refreshed. Pulled: ${[s.name, s.title].filter(Boolean).join(" · ")}`
          : "LinkedIn blocked the public scrape — only URL saved. Try uploading a LinkedIn PDF instead ↓",
      );
    } catch (e) {
      setLinkedinMsg(`Refresh failed: ${e.response?.data?.detail || e.message}. URL still saved if valid.`);
    } finally { setLinkedinBusy(false); }
  };

  const uploadLinkedinPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLinkedinPdfBusy(true); setLinkedinMsg(null);
    try {
      const form = new FormData();
      form.append("file", f);
      const token = localStorage.getItem("vaku_access_token");
      const res = await fetch(`${API}/candidates/${candidate.id}/linkedin-pdf`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      const imp = data.import || {};
      const bits = [
        imp.name && `name`,
        imp.headline && `headline`,
        imp.location && `location`,
        (imp.skills?.length || 0) > 0 && `${imp.skills.length} skills`,
        (imp.experience?.length || 0) > 0 && `${imp.experience.length} roles`,
        (imp.education?.length || 0) > 0 && `${imp.education.length} education`,
      ].filter(Boolean).join(", ");
      setLinkedinMsg(`✓ LinkedIn PDF imported (${bits || "text saved"}). Empty fields on the profile were auto-filled.`);
      await refreshSelected();
      await onSynced();
    } catch (err) {
      setLinkedinMsg(`PDF import failed: ${err.message}`);
    } finally {
      setLinkedinPdfBusy(false);
      if (linkedinPdfRef.current) linkedinPdfRef.current.value = "";
    }
  };

  const extendOffer = async (payload) => {
    setOfferBusy(true); setOfferMsg(null);
    try {
      const url = offerCounterMode
        ? `/candidates/${candidate.id}/offer/counter`
        : `/candidates/${candidate.id}/offer`;
      const r = await http.post(url, payload);
      const em = offerCounterMode ? r.data?.counterEmail : r.data?.offerEmail;
      const emailNote = em?.sent
        ? ` Email sent to ${(em.to || []).join(", ")}.`
        : em?.skipped
          ? ` ${em.reason ? `(Email skipped: ${em.reason})` : ""}`
          : em?.error
            ? ` (Email failed: ${em.error})`
            : "";
      const verb = offerCounterMode ? "Counter offer sent (revision reset the candidate response)" : "Offer extended. Candidate → Offer status.";
      setOfferMsg(verb + emailNote);
      await refreshSelected();
      await onSynced();
      setOfferOpen(false);
      setOfferCounterMode(false);
    } catch (e) {
      setOfferMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setOfferBusy(false); }
  };

  const respondOffer = async (accepted) => {
    const reason = window.prompt(
      accepted ? "Optional acceptance note?" : "Reason for rejection?",
      accepted ? "" : "Declined",
    );
    if (reason === null) return;
    setOfferBusy(true); setOfferMsg(null);
    try {
      const r = await http.post(`/candidates/${candidate.id}/offer/response`, { accepted, reason });
      setOfferMsg(
        `Offer ${accepted ? "accepted → Hired" : "rejected"}.${notifyNote}`,
      );
      const re = r.data?.recruiterEmail;
      const notifyNote = re?.sent
        ? ` Recruiter notified at ${(re.to || []).join(", ")}.`
        : re?.error
          ? ` (Recruiter email failed: ${re.error})`
          : re?.reason
            ? ` (Recruiter email skipped: ${re.reason})`
            : "";
      setOfferMsg(
        `Offer ${accepted ? "accepted → Hired" : "rejected"}.${notifyNote}`,
      );
      await refreshSelected();
      await onSynced();
    } catch (e) {
      setOfferMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setOfferBusy(false); }
  };

  const holdOffer = async (payload) => {
    setOfferBusy(true); setOfferMsg(null);
    try {
      const r = await http.post(`/candidates/${candidate.id}/offer/hold`, payload);
      const em = r.data?.holdEmail;
      const note = em?.sent ? ` Candidate notified at ${(em.to || []).join(", ")}.`
        : em?.error ? ` (Email failed: ${em.error})` : "";
      setOfferMsg("Offer placed on hold." + note);
      await refreshSelected();
      await onSynced();
      setHoldOpen(false);
    } catch (e) {
      setOfferMsg(`Hold failed: ${e.response?.data?.detail || e.message}`);
    } finally { setOfferBusy(false); }
  };

  const releaseHold = async () => {
    const comments = window.prompt("Optional message to include with the release?", "");
    if (comments === null) return;
    setOfferBusy(true); setOfferMsg(null);
    try {
      const r = await http.post(`/candidates/${candidate.id}/offer/release`, { comments });
      const em = r.data?.releaseEmail;
      const note = em?.sent ? ` Candidate notified.` : em?.error ? ` (Email failed: ${em.error})` : "";
      setOfferMsg("Offer hold released — candidate can respond again." + note);
      await refreshSelected();
      await onSynced();
    } catch (e) {
      setOfferMsg(`Release failed: ${e.response?.data?.detail || e.message}`);
    } finally { setOfferBusy(false); }
  };

  const deactivate = async () => {
    const reason = window.prompt("Reason for deactivation? (e.g. Withdrawn, Hired elsewhere, Not a fit)", "Withdrawn");
    if (reason === null) return;
    setLifecycleBusy(true); setLifecycleMsg(null);
    try {
      await http.post(`/candidates/${candidate.id}/deactivate`, { reason });
      setLifecycleMsg("Candidate deactivated.");
      await refreshSelected();
      await onSynced();
    } catch (e) {
      setLifecycleMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLifecycleBusy(false);
    }
  };

  const reactivate = async () => {
    setLifecycleBusy(true); setLifecycleMsg(null);
    try {
      await http.post(`/candidates/${candidate.id}/reactivate`);
      setLifecycleMsg("Reactivated.");
      await refreshSelected();
      await onSynced();
    } catch (e) {
      setLifecycleMsg(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLifecycleBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${candidate.name}? This permanently removes the candidate from Vaku. This action cannot be undone.`)) return;
    setLifecycleBusy(true); setLifecycleMsg(null);
    try {
      await http.delete(`/candidates/${candidate.id}`);
      await onSynced();
      onClose();
    } catch (e) {
      setLifecycleMsg(`Delete failed: ${e.response?.data?.detail || e.message}`);
      setLifecycleBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    http.get("/fusion/requisitions").then((r) => {
      if (!alive) return;
      setReqs(r.data?.items || []);
      setReqsSource(r.data?.source || null);
      setReqsHint(r.data?.hint || null);
      setReqsLoading(false);
    }).catch(() => alive && setReqsLoading(false));
    return () => { alive = false; };
  }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await http.post(`/fusion/sync/candidate/${candidate.id}`);
      setLastResult(r.data.sync);
      await refreshSelected();
      await onSynced();
    } catch (e) {
      setLastResult({ ok: false, error: e.message });
    } finally {
      setSyncing(false);
    }
  };

  const submitApp = async () => {
    if (!reqNumInput.trim()) return;
    setSubmittingApp(true);
    setAppError(null);
    try {
      const r = await http.post(`/fusion/sync/application/${candidate.id}`, {
        requisitionNumber: reqNumInput.trim(),
      });
      if (!r.data.sync.ok) setAppError(r.data.sync.error || "Submission failed");
      await refreshSelected();
      await onSynced();
    } catch (e) {
      setAppError(e.response?.data?.detail || e.message);
    } finally {
      setSubmittingApp(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="candidate-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white border-l h-full overflow-y-auto p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-eyebrow">Candidate · #{String(candidate.id).padStart(3, "0")}</div>
            <h2 className="font-display text-2xl font-bold mt-1">{candidate.name}</h2>
            <div className="text-slate-500 text-sm">{candidate.position} · {candidate.location}</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="drawer-edit-profile"
              className="btn btn-outline !py-1 !px-2 !text-xs"
              onClick={() => setEditProfileOpen(true)}
              title="Edit candidate profile"
            >
              <PencilSimple size={12} /> Edit
            </button>
            <button onClick={onClose} className="btn-ghost p-1" data-testid="drawer-close"><X size={18} /></button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
          <Detail label="Status" value={<span className={`pill ${STATUS_PILL[candidate.status]}`}>{candidate.status}</span>} />
          <Detail label="Match Score" value={`${candidate.matchScore}/100`} />
          <Detail label="Experience" value={candidate.experience || "—"} />
          <Detail label="Application Date" value={candidate.applicationDate} />
          <Detail label="Email" value={candidate.email || "—"} />
          <Detail label="Phone" value={candidate.phone || "—"} />
        </div>
        <div className="mt-5">
          <div className="label-eyebrow mb-2">Skills</div>
          <div className="flex flex-wrap gap-1.5">
            {(candidate.skills || []).map((s) => (
              <span key={s} className="px-2 py-0.5 text-xs bg-slate-100 border rounded-sm">{s}</span>
            ))}
            {(!candidate.skills || candidate.skills.length === 0) && (
              <span className="text-xs text-slate-400">No skills recorded.</span>
            )}
          </div>
        </div>

        {/* LinkedIn edit / refresh */}
        <div className="mt-6 p-4 border bg-[#fafbfd] rounded-sm" data-testid="drawer-linkedin-section">
          <div className="flex items-center justify-between mb-2">
            <div className="label-eyebrow flex items-center gap-1.5">
              <LinkedinLogo size={12} weight="fill" className="text-[#0A66C2]" /> LinkedIn Profile
            </div>
            {candidate.linkedinUrl && (
              <a
                href={candidate.linkedinUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="text-[11px] text-[#0A66C2] hover:underline"
                data-testid="drawer-linkedin-open"
              >
                Open profile ↗
              </a>
            )}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="drawer-linkedin-url"
              className="input !py-1.5 !text-xs flex-1"
              placeholder="https://www.linkedin.com/in/username"
              value={linkedinDraft}
              onChange={(e) => setLinkedinDraft(e.target.value)}
              disabled={linkedinBusy}
            />
            <button
              data-testid="drawer-linkedin-save"
              className="btn btn-outline !py-1.5 !px-3 !text-xs whitespace-nowrap"
              onClick={saveLinkedin}
              disabled={linkedinBusy || linkedinDraft.trim() === (candidate.linkedinUrl || "").trim()}
            >
              {linkedinBusy ? "…" : "Save"}
            </button>
            <button
              data-testid="drawer-linkedin-refresh"
              className="btn btn-primary !py-1.5 !px-3 !text-xs whitespace-nowrap"
              onClick={refreshLinkedin}
              disabled={linkedinBusy || !linkedinDraft.trim()}
              title="Save the URL AND fetch public profile data"
            >
              {linkedinBusy ? "Fetching…" : "Save & Fetch"}
            </button>
          </div>
          {candidate.linkedinRefreshedAt && (
            <div className="text-[10px] text-slate-400 mt-1">
              Last refreshed: {new Date(candidate.linkedinRefreshedAt).toLocaleString()}
            </div>
          )}
          {/* LinkedIn PDF import — bypasses LinkedIn's public-scrape block */}
          <div className="mt-3 rounded-sm border border-dashed border-[#0A66C2]/40 bg-[#0A66C2]/[0.03] px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-600 leading-snug">
                <strong className="text-[#0A66C2]">Import from LinkedIn PDF</strong> — export the profile from LinkedIn (More → <em>Save to PDF</em>) and upload it here to auto-fill headline, summary, skills, experience & education.
              </div>
              <input
                ref={linkedinPdfRef}
                type="file"
                accept=".pdf"
                data-testid="drawer-linkedin-pdf-input"
                onChange={uploadLinkedinPdf}
                className="hidden"
              />
              <button
                data-testid="drawer-linkedin-pdf-btn"
                className="btn btn-outline !py-1.5 !px-3 !text-xs whitespace-nowrap !text-[#0A66C2] !border-[#0A66C2]/40 hover:!bg-[#0A66C2]/[0.06]"
                disabled={linkedinPdfBusy || linkedinBusy}
                onClick={() => linkedinPdfRef.current?.click()}
              >
                <FilePdf size={13} weight="fill" />
                {linkedinPdfBusy ? "Reading…" : "Upload PDF"}
              </button>
            </div>
          </div>
          {(candidate.linkedin?.headline || candidate.linkedin?.summary) && (
            <div className="mt-2 rounded-sm border border-slate-200 bg-white p-2 text-[11px] space-y-1" data-testid="drawer-linkedin-data">
              {candidate.linkedin?.headline && (
                <div className="text-slate-700"><strong>Headline:</strong> {candidate.linkedin.headline}</div>
              )}
              {candidate.linkedin?.summary && (
                <div className="text-slate-500 line-clamp-3">{candidate.linkedin.summary}</div>
              )}
            </div>
          )}
          {linkedinMsg && (
            <div className="text-[11px] text-slate-700 mt-2 bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid="drawer-linkedin-msg">
              {linkedinMsg}
            </div>
          )}
        </div>

        {/* Fusion drawer section intentionally removed — Vaku manages
            candidates locally. */}

        {candidate.offer && (
          <div className={`mt-4 rounded-md border p-3 text-sm ${
            candidate.offer.state === "held"
              ? "border-amber-300 bg-amber-50"
              : "border-emerald-200 bg-emerald-50/50"
          }`} data-testid="offer-section">
            <div className={`font-display font-bold flex items-center gap-2 ${candidate.offer.state === "held" ? "text-amber-800" : "text-emerald-800"}`}>
              Offer {candidate.offer.state && <span className="pill pill-offer !text-[10px] uppercase">{candidate.offer.state}</span>}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs text-slate-700">
              <div>Salary: <span className="font-mono">{candidate.offer.salary || "—"} {candidate.offer.currency}</span></div>
              <div>Start: <span className="font-mono">{candidate.offer.startDate || "—"}</span></div>
              <div>Expires: <span className="font-mono">{candidate.offer.expiryDate || "—"}</span></div>
              <div>Extended by: <span>{candidate.offer.extendedBy || "—"}</span></div>
            </div>
            {candidate.offer.notes && <div className="text-xs text-slate-500 mt-1">{candidate.offer.notes}</div>}

            {/* Active hold banner */}
            {candidate.offer.state === "held" && candidate.offer.hold && (
              <div className="mt-3 rounded border border-amber-300 bg-white/60 px-2 py-2 text-xs" data-testid="offer-hold-info">
                <div className="font-semibold text-amber-900">
                  On hold: {candidate.offer.hold.reason}
                </div>
                <div className="text-slate-600 mt-1 grid grid-cols-2 gap-x-4">
                  <div>Held by: <span>{candidate.offer.hold.heldByName || candidate.offer.hold.heldBy || "—"}</span></div>
                  <div>Held at: <span className="font-mono">{candidate.offer.hold.heldAt ? new Date(candidate.offer.hold.heldAt).toLocaleString() : "—"}</span></div>
                  <div>Expected release: <span className="font-mono">{candidate.offer.hold.holdUntil || "—"}</span></div>
                </div>
                {candidate.offer.hold.comments && (
                  <div className="text-slate-500 mt-1 italic">&ldquo;{candidate.offer.hold.comments}&rdquo;</div>
                )}
              </div>
            )}

            {candidate.offerResponse ? (
              <div className={`mt-2 text-xs ${candidate.offerResponse.accepted ? "text-emerald-800" : "text-red-800"}`} data-testid="offer-response">
                <strong>Candidate {candidate.offerResponse.accepted ? "accepted" : "declined"}</strong> on {new Date(candidate.offerResponse.respondedAt).toLocaleString()}
                {candidate.offerResponse.reason && ` — "${candidate.offerResponse.reason}"`}
                {!candidate.offerResponse.accepted && (
                  <div className="mt-2">
                    <button
                      data-testid="offer-counter"
                      className="btn btn-outline !py-1 !text-xs !text-indigo-700 !border-indigo-300 hover:!bg-indigo-50"
                      onClick={() => { setOfferCounterMode(true); setOfferOpen(true); }}
                      disabled={offerBusy}
                    >
                      Send counter offer
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-3 flex gap-2 flex-wrap">
                {candidate.offer.state === "extended" && (
                  <>
                    <button
                      data-testid="offer-accept"
                      className="btn btn-primary !py-1 !text-xs"
                      onClick={() => respondOffer(true)}
                      disabled={offerBusy}
                    >
                      Mark Accepted
                    </button>
                    <button
                      data-testid="offer-reject"
                      className="btn btn-outline !py-1 !text-xs !text-red-700 !border-red-300 hover:!bg-red-50"
                      onClick={() => respondOffer(false)}
                      disabled={offerBusy}
                    >
                      Mark Rejected
                    </button>
                    <button
                      data-testid="offer-hold"
                      className="btn btn-outline !py-1 !text-xs !text-amber-800 !border-amber-300 hover:!bg-amber-50"
                      onClick={() => setHoldOpen(true)}
                      disabled={offerBusy}
                    >
                      Hold Offer
                    </button>
                  </>
                )}
                {candidate.offer.state === "held" && (
                  <button
                    data-testid="offer-release"
                    className="btn btn-primary !py-1 !text-xs"
                    onClick={releaseHold}
                    disabled={offerBusy}
                  >
                    Release Hold
                  </button>
                )}
              </div>
            )}
            {offerMsg && <div className="mt-2 text-xs text-slate-700">{offerMsg}</div>}
          </div>
        )}

        <div className="mt-6 flex gap-2 flex-wrap" data-testid="drawer-actions">
          <button className="btn btn-primary" onClick={onScreen} data-testid="drawer-screen">Run AI Screening</button>
          {candidate.active !== false && !candidate.offer && (
            <button
              data-testid="drawer-extend-offer"
              className="btn btn-outline !text-emerald-700 !border-emerald-300 hover:!bg-emerald-50"
              onClick={() => setOfferOpen(true)}
              disabled={lifecycleBusy}
            >
              Extend Offer
            </button>
          )}
          {candidate.active === false ? (
            <button
              data-testid="drawer-reactivate"
              className="btn btn-outline"
              onClick={reactivate}
              disabled={lifecycleBusy}
              title="Reactivate this candidate"
            >
              <ArrowCounterClockwise size={14} /> {lifecycleBusy ? "Working…" : "Reactivate"}
            </button>
          ) : (
            <button
              data-testid="drawer-deactivate"
              className="btn btn-outline"
              onClick={deactivate}
              disabled={lifecycleBusy}
              title="Soft-deactivate this candidate (recommended)"
            >
              <UserMinus size={14} /> {lifecycleBusy ? "Working…" : "Deactivate"}
            </button>
          )}
          {isAdmin && (
            <button
              data-testid="drawer-delete"
              className="btn btn-outline !text-red-700 !border-red-300 hover:!bg-red-50"
              onClick={remove}
              disabled={lifecycleBusy}
              title="Hard delete (admin only)"
            >
              <Trash size={14} /> Delete
            </button>
          )}
        </div>
        {lifecycleMsg && (
          <div className="mt-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-sm p-2" data-testid="lifecycle-msg">
            {lifecycleMsg}
          </div>
        )}
        {candidate.active === false && (
          <div className="mt-3 rounded-sm border border-slate-300 bg-slate-50 p-3 text-xs text-slate-700" data-testid="inactive-info">
            <div className="font-semibold text-slate-800">Inactive candidate</div>
            <div className="mt-1">End date: <span className="font-mono">{candidate.endDate || "—"}</span></div>
            <div>Reason: {candidate.deactivationReason || "—"}</div>
          </div>
        )}
      </div>
      {offerOpen && (
        <ExtendOfferModal
          candidate={candidate}
          busy={offerBusy}
          counterMode={offerCounterMode}
          onClose={() => { setOfferOpen(false); setOfferCounterMode(false); }}
          onExtend={extendOffer}
        />
      )}
      {holdOpen && (
        <HoldOfferModal
          candidate={candidate}
          busy={offerBusy}
          onClose={() => setHoldOpen(false)}
          onHold={holdOffer}
        />
      )}
      {editProfileOpen && (
        <EditProfileModal
          candidate={candidate}
          onClose={() => setEditProfileOpen(false)}
          onSaved={async () => {
            setEditProfileOpen(false);
            await refreshSelected();
            await onSynced();
          }}
        />
      )}
    </div>
  );
}

function EditProfileModal({ candidate, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: candidate.name || "",
    email: candidate.email || "",
    phone: candidate.phone || "",
    position: candidate.position || "",
    location: candidate.location || "",
    experience: candidate.experience || "",
    skills: (candidate.skills || []).join(", "),
    matchScore: candidate.matchScore || 0,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await http.patch(`/candidates/${candidate.id}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        position: form.position.trim(),
        location: form.location.trim(),
        experience: form.experience.trim(),
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        matchScore: parseInt(form.matchScore, 10) || 0,
      });
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50" data-testid="edit-profile-modal">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Edit Candidate Profile</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Full Name</label>
            <input data-testid="edit-name" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Email</label>
            <input data-testid="edit-email" type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="candidate@example.com" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Phone</label>
            <input data-testid="edit-phone" className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Position</label>
            <input data-testid="edit-position" className="input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Location</label>
            <select className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
              {["Riyadh", "Jeddah", "Dammam", "Remote", "Other"].map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Experience</label>
            <input className="input" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Match Score (0-100)</label>
            <input type="number" min="0" max="100" className="input" value={form.matchScore} onChange={(e) => setForm({ ...form, matchScore: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Skills (comma separated)</label>
            <input className="input" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} placeholder="HCM Payroll, Compliance, Excel" />
          </div>
        </div>
        {err && <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button data-testid="edit-profile-save" className="btn btn-primary" onClick={save} disabled={busy || !form.name.trim()}>
            {busy ? "Saving…" : "Save Profile"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExtendOfferModal({ candidate, busy, counterMode = false, onClose, onExtend }) {
  const prev = candidate.offer || {};
  const [form, setForm] = useState({
    salary: counterMode && prev.salary ? String(prev.salary) : "",
    currency: (counterMode && prev.currency) || "SAR",
    startDate: (counterMode && prev.startDate) || "",
    expiryDate: "",
    position: (counterMode && prev.position) || candidate.position || "",
    notes: "",
  });
  // Warn recruiter if this jumps past Interview stage.
  const preOfferOk = ["Interview", "Screening"].includes(candidate.status);
  const skipWarning = !counterMode && !preOfferOk && candidate.status !== "Offer";
  const declineReason = candidate.offerResponse?.reason;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50" data-testid="extend-offer-modal">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">
            {counterMode ? `Counter Offer — ${candidate.name}` : `Extend Offer — ${candidate.name}`}
          </h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        {counterMode && (
          <div className="mb-3 rounded-md border border-indigo-300 bg-indigo-50 p-3 text-xs text-indigo-900" data-testid="counter-offer-context">
            <div><strong>Previous offer:</strong> {prev.salary ? `${prev.salary} ${prev.currency || ""}` : "—"}</div>
            {declineReason && <div className="mt-1"><strong>Decline reason:</strong> &ldquo;{declineReason}&rdquo;</div>}
            <div className="mt-1 text-indigo-700">Sending a counter offer will archive the previous offer + response and reopen the offer state.</div>
          </div>
        )}
        {skipWarning && (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="skip-stage-warning">
            <strong>Skipping Interview stage.</strong> Candidate is currently in <em>{candidate.status}</em>. Extending an offer directly bypasses screening & interviews. This will be audit-logged as a direct promotion.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1">Salary</label>
            <input data-testid="offer-salary" type="number" className="input" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder="e.g. 24000" />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Currency</label>
            <select className="input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              {["SAR", "USD", "AED", "EUR", "GBP", "INR"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Start Date</label>
            <input data-testid="offer-start" type="date" className="input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Offer Expiry</label>
            <input data-testid="offer-expiry" type="date" className="input" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Position</label>
            <input className="input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Notes / Package Details</label>
            <textarea data-testid="offer-notes" className="input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Benefits, sign-on bonus, relocation, etc." />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            data-testid="offer-extend-submit"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onExtend({
              salary: parseFloat(form.salary) || null,
              currency: form.currency,
              startDate: form.startDate || null,
              expiryDate: form.expiryDate || null,
              position: form.position,
              notes: form.notes,
            })}
          >
            {busy ? (counterMode ? "Sending…" : "Extending…") : (counterMode ? "Send Counter Offer" : "Extend Offer")}
          </button>
        </div>
      </div>
    </div>
  );
}

const HOLD_REASONS = [
  "Awaiting budget approval",
  "Awaiting internal alignment",
  "Requisition on freeze",
  "Background check in progress",
  "Compensation review",
  "Candidate requested pause",
  "Other",
];

function HoldOfferModal({ candidate, busy, onClose, onHold }) {
  const [form, setForm] = useState({
    reason: HOLD_REASONS[0],
    customReason: "",
    holdUntil: "",
    comments: "",
  });
  const isCustom = form.reason === "Other";
  const effectiveReason = isCustom ? form.customReason.trim() : form.reason;
  const canSubmit = !!effectiveReason && !busy;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50" data-testid="hold-offer-modal">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Hold Offer — {candidate.name}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          Placing the offer on hold pauses candidate Accept/Reject. We&apos;ll send them a courtesy note so they don&apos;t worry. You can release the hold anytime.
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-1">Reason</label>
            <select
              data-testid="hold-reason"
              className="input"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            >
              {HOLD_REASONS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          {isCustom && (
            <div>
              <label className="label-eyebrow block mb-1">Custom Reason</label>
              <input
                data-testid="hold-custom-reason"
                className="input"
                value={form.customReason}
                placeholder="Describe the reason for the hold"
                onChange={(e) => setForm({ ...form, customReason: e.target.value })}
              />
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-1">Expected Release Date <span className="text-slate-400">(optional)</span></label>
            <input
              data-testid="hold-until"
              type="date"
              className="input"
              value={form.holdUntil}
              onChange={(e) => setForm({ ...form, holdUntil: e.target.value })}
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Comments for the candidate <span className="text-slate-400">(optional)</span></label>
            <textarea
              data-testid="hold-comments"
              className="input"
              rows={4}
              value={form.comments}
              placeholder="e.g. We're finalizing sign-off with Finance; expect to reach out again next week."
              onChange={(e) => setForm({ ...form, comments: e.target.value })}
            />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            data-testid="hold-submit"
            className="btn btn-primary !bg-amber-600 hover:!bg-amber-700"
            disabled={!canSubmit}
            onClick={() => onHold({
              reason: effectiveReason,
              comments: form.comments,
              holdUntil: form.holdUntil || null,
            })}
          >
            {busy ? "Holding…" : "Place on Hold"}
          </button>
        </div>
      </div>
    </div>
  );
}


function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className={mono ? "font-mono" : ""}>{value}</span>
    </div>
  );
}

function SyncLogModal({ onClose }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    http.get("/fusion/sync/log", { params: { limit: 50 } }).then((r) => setEntries(r.data));
  }, []);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40" data-testid="sync-log-modal">
      <div className="card w-full max-w-3xl p-6 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-bold">Fusion HCM Sync Log</h2>
            <div className="text-xs text-slate-500">Last {entries.length} sync attempts</div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Candidate</th>
                <th>Action</th>
                <th>Result</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i}>
                  <td className="font-mono text-[11px]">{new Date(e.ts).toLocaleString()}</td>
                  <td className="font-mono">#{e.candidateId}</td>
                  <td>{e.action}</td>
                  <td>
                    {e.ok ? (
                      <span className="pill pill-offer">OK · {e.status_code || 200}</span>
                    ) : (
                      <span className="pill pill-rejected">{e.status_code || "ERR"}</span>
                    )}
                  </td>
                  <td className="text-[11px] text-slate-500 max-w-[300px] truncate" title={e.detail || e.error}>
                    {e.detail || e.error || "—"}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">No sync attempts yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow !text-[10px] mb-0.5">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

function AddCandidateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", position: "", location: "Riyadh",
    email: "", phone: "", skills: "", experience: "", matchScore: 70, status: "New",
    requisitionNumber: "",
    linkedinUrl: "",
  });
  const [linkedinFetching, setLinkedinFetching] = useState(false);
  const [linkedinPreview, setLinkedinPreview] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [reqsSource, setReqsSource] = useState(null); // 'fusion' | 'seed' | null
  const [reqsHint, setReqsHint] = useState(null);
  const [reqsLoading, setReqsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let alive = true;
    http.get("/fusion/requisitions").then((r) => {
      if (!alive) return;
      setReqs(r.data?.items || []);
      setReqsSource(r.data?.source || null);
      setReqsHint(r.data?.hint || null);
      setReqsLoading(false);
    }).catch(() => alive && setReqsLoading(false));
    return () => { alive = false; };
  }, []);

  const missing = [];
  if (!form.name.trim()) missing.push("Name");
  if (!form.position.trim()) missing.push("Position");
  if (reqsSource === "fusion" && reqs.length > 0 && !form.requisitionNumber) missing.push("Requisition");
  const isDisabled = saving || missing.length > 0;

  const save = async () => {
    setSaving(true);
    setResult(null);
    setSaveError(null);
    try {
      const { data } = await http.post("/candidates", {
        ...form,
        matchScore: parseInt(form.matchScore, 10) || 0,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        requisitionNumber: form.requisitionNumber || null,
      });
      setResult(data?.fusionSync || null);
      await onCreated();
      const candOk = data?.fusionSync?.candidate?.ok;
      const appOk = data?.fusionSync?.application?.ok;
      // Auto-close only when there are no Fusion errors to display.
      if (candOk && (appOk || !form.requisitionNumber)) {
        setTimeout(onClose, 800);
      }
    } catch (e) {
      setSaveError(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40" data-testid="add-candidate-modal">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Add New Candidate</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" testid="new-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Position" testid="new-position" value={form.position} onChange={(v) => setForm({ ...form, position: v })} />
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">
              Requisition <span className="text-red-600">*</span> <span className="text-slate-400">(required for Fusion visibility)</span>
            </label>
            <select
              data-testid="new-requisition"
              className="input"
              value={form.requisitionNumber}
              onChange={(e) => setForm({ ...form, requisitionNumber: e.target.value })}
              disabled={reqsLoading || reqs.length === 0}
            >
              <option value="">
                {reqsLoading ? "Loading requisitions…" :
                  reqs.length === 0 ? "— No requisitions visible —" :
                  "— Select a requisition —"}
              </option>
              {reqs.map((r) => (
                <option key={r.RequisitionNumber} value={r.RequisitionNumber}>
                  {r.RequisitionNumber} — {r.Title} {r.Status ? `(${r.Status})` : ""}
                </option>
              ))}
            </select>
            {reqsSource === "fusion" && reqs.length === 0 && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900" data-testid="reqs-empty-warning">
                <strong>Fusion is connected but returned 0 requisitions.</strong>
                <div className="mt-1">{reqsHint || "Check that your requisition is in Posted state and your Fusion REST user has the Recruiter/Recruiting Manager data role."}</div>
              </div>
            )}
            {reqsSource === "fusion" && reqs.length > 0 && (
              <div className="text-xs text-emerald-700 mt-1">Live Fusion requisitions ({reqs.length})</div>
            )}
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Location</label>
            <select className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
              {["Riyadh", "Jeddah", "Dammam"].map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
          <Field label="Email" testid="new-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Field label="Phone" testid="new-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Experience" testid="new-exp" value={form.experience} onChange={(v) => setForm({ ...form, experience: v })} />
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">Skills (comma separated)</label>
            <input data-testid="new-skills" className="input" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
          </div>
          <Field label="Match Score (0-100)" testid="new-score" value={form.matchScore} onChange={(v) => setForm({ ...form, matchScore: v })} />
          <div className="col-span-2">
            <label className="label-eyebrow block mb-1">LinkedIn Profile URL <span className="text-slate-400">(optional)</span></label>
            <div className="flex gap-2">
              <input
                data-testid="new-linkedin-url"
                className="input flex-1"
                placeholder="https://www.linkedin.com/in/username"
                value={form.linkedinUrl}
                onChange={(e) => setForm({ ...form, linkedinUrl: e.target.value })}
              />
              <button
                data-testid="new-linkedin-fetch"
                className="btn btn-outline !text-xs whitespace-nowrap"
                disabled={!form.linkedinUrl || linkedinFetching}
                onClick={async () => {
                  setLinkedinFetching(true);
                  setLinkedinPreview(null);
                  try {
                    const { data } = await http.post("/candidates/profile-preview", { url: form.linkedinUrl });
                    setLinkedinPreview(data);
                    if (data?.title && !form.position) setForm((f) => ({ ...f, position: data.title.split("—")[0].trim() }));
                    if (data?.name && !form.name) setForm((f) => ({ ...f, name: data.name }));
                  } catch (e) {
                    setLinkedinPreview({ error: e.response?.data?.detail || e.message });
                  } finally {
                    setLinkedinFetching(false);
                  }
                }}
              >
                {linkedinFetching ? "Fetching…" : "Fetch"}
              </button>
            </div>
            {linkedinPreview && !linkedinPreview.error && (
              <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2 text-xs" data-testid="linkedin-preview">
                <div className="font-semibold">{linkedinPreview.name || "—"}</div>
                <div className="text-slate-600">{linkedinPreview.title || "no title in public preview"}</div>
                {linkedinPreview.description && (
                  <div className="text-slate-500 mt-1 line-clamp-2">{linkedinPreview.description}</div>
                )}
              </div>
            )}
            {linkedinPreview?.error && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                LinkedIn preview failed: {linkedinPreview.error}. URL will still be saved with the candidate.
              </div>
            )}
          </div>
        </div>

        {result && result.application?.hint && !result.application?.ok && (
          <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="fusion-app-hint">
            <strong>Fusion note:</strong> {result.application.hint}
          </div>
        )}

        {saveError && (
          <div className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="save-error">
            <strong>Save failed:</strong> {saveError}
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          {missing.length > 0 && (
            <span className="text-xs text-amber-700 mr-auto" data-testid="missing-fields-hint">
              Required: {missing.join(", ")}
            </span>
          )}
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            data-testid="new-candidate-save"
            className={`btn btn-primary ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={save}
            disabled={isDisabled}
          >
            {saving ? "Saving & syncing…" : reqsSource === "seed" || reqs.length === 0 ? "Save (pool only)" : "Save & Sync to Fusion"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}</label>
      <input data-testid={testid} className="input" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
